import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { Percent, RefreshCw, Edit3, Trash2, Copy, X, Eye, MousePointerClick } from 'lucide-react';

/**
 * Admin Offers Tab
 *
 * Self-contained admin view for cross-business offer management. Mirrors the
 * permissions of the admin Events tab — admins can edit any field, recreate,
 * or delete an offer, bypassing the business-side date-extension restriction.
 *
 * Backend contracts (already deployed):
 *   GET    /api/admin/offers?expired=...&page=...&limit=...
 *   PUT    /api/admin/offer/{id}                 — accepts any field
 *   DELETE /api/admin/offer/{id}
 *   POST   /api/admin/offer/{id}/duplicate
 */
const AdminOffersTab = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all' | 'active' | 'expired'
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editModal, setEditModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (filter === 'active') params.expired = 'exclude';
      else if (filter === 'expired') params.expired = 'only';
      const res = await axiosInstance.get('/admin/offers', { params });
      setOffers(res.data.items || []);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      setMessage({ type: 'error', text: `Error loading offers: ${err.response?.data?.detail || err.message}` });
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => { fetchOffers(); }, [fetchOffers]);

  const flash = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const saveEdit = async () => {
    if (!editModal) return;
    setActionLoading(`edit-${editModal.id}`);
    try {
      const payload = {
        title: editModal.title,
        description: editModal.description,
        discountPercentage: editModal.discountPercentage != null ? Number(editModal.discountPercentage) : null,
        validUntil: editModal.validUntil,
        terms: editModal.terms || '',
        isVisible: editModal.isVisible !== false,
      };
      await axiosInstance.put(`/admin/offer/${editModal.id}`, payload);
      flash('success', `Offer "${editModal.title}" updated.`);
      setEditModal(null);
      await fetchOffers();
    } catch (err) {
      flash('error', err.response?.data?.detail || err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const deleteOffer = async () => {
    if (!confirmDelete) return;
    setActionLoading(`delete-${confirmDelete.id}`);
    try {
      await axiosInstance.delete(`/admin/offer/${confirmDelete.id}`);
      flash('success', `Offer "${confirmDelete.title}" deleted.`);
      setConfirmDelete(null);
      await fetchOffers();
    } catch (err) {
      flash('error', err.response?.data?.detail || err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const recreateOffer = async (offer) => {
    setActionLoading(`duplicate-${offer.id}`);
    try {
      const res = await axiosInstance.post(`/admin/offer/${offer.id}/duplicate`);
      flash('success', `Offer recreated. Edit dates on the new copy now.`);
      await fetchOffers();
      const newId = res.data?.id;
      if (newId) {
        // Re-fetch the new doc and open it for editing
        const list = await axiosInstance.get('/admin/offers', { params: { page: 1, limit: 50 } });
        const newDoc = (list.data.items || []).find(o => o.id === newId);
        if (newDoc) setEditModal({ ...newDoc });
      }
    } catch (err) {
      flash('error', err.response?.data?.detail || err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const dateForInput = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toISOString().split('T')[0]; } catch { return ''; }
  };

  return (
    <div className="space-y-4" data-testid="admin-offers-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Percent className="h-5 w-5 text-orange-500" /> Offers
        </h2>
        <div className="flex gap-2">
          {['all', 'active', 'expired'].map(f => (
            <button
              key={f}
              onClick={() => { setFilter(f); setPage(1); }}
              className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${
                filter === f ? 'bg-orange-500 text-white' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
              data-testid={`admin-offers-filter-${f}`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {message.text && (
        <div className={`px-4 py-2 rounded-md text-sm ${message.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`} data-testid="admin-offers-message">
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><RefreshCw className="h-6 w-6 animate-spin text-orange-500" /></div>
      ) : offers.length === 0 ? (
        <div className="text-center py-10 text-gray-500 bg-white rounded-xl shadow-sm">No offers found.</div>
      ) : (
        <div className="space-y-3">
          {offers.map(offer => (
            <div key={offer.id} className={`rounded-xl shadow-sm border p-4 ${offer.isExpired ? 'bg-gray-50 border-gray-200' : 'bg-white border-orange-100'}`} data-testid={`admin-offer-row-${offer.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900 truncate">{offer.title}</h3>
                    {offer.discountPercentage > 0 && (
                      <span className="px-2 py-0.5 text-xs font-bold bg-orange-100 text-orange-700 rounded-full">
                        -{offer.discountPercentage}%
                      </span>
                    )}
                    {offer.isExpired && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded-full">Expired</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    <span className="font-medium">{offer.businessName || '—'}</span>
                    {offer.businessCity && <> · {offer.businessCity}</>}
                  </p>
                  {offer.description && (
                    <p className="text-sm text-gray-700 mt-2 line-clamp-2">{offer.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                    <span>Valid until: {new Date(offer.validUntil).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{offer.views || 0}</span>
                    <span className="flex items-center gap-1"><MousePointerClick className="h-3 w-3" />{offer.clicks || 0}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                <button
                  onClick={() => setEditModal({ ...offer })}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100"
                  data-testid={`admin-edit-offer-btn-${offer.id}`}
                >
                  <Edit3 className="h-3 w-3" /> Edit
                </button>
                <button
                  onClick={() => recreateOffer(offer)}
                  disabled={actionLoading === `duplicate-${offer.id}`}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50"
                  data-testid={`admin-duplicate-offer-btn-${offer.id}`}
                  title="Create a new offer from this one — set new dates"
                >
                  {actionLoading === `duplicate-${offer.id}` ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Copy className="h-3 w-3" />}
                  Recreate
                </button>
                <button
                  onClick={() => setConfirmDelete({ id: offer.id, title: offer.title })}
                  disabled={actionLoading === `delete-${offer.id}`}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100 disabled:opacity-50"
                  data-testid={`admin-delete-offer-btn-${offer.id}`}
                >
                  {actionLoading === `delete-${offer.id}` ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-3 pt-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-md disabled:opacity-50">Prev</button>
          <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
            className="px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-md disabled:opacity-50">Next</button>
        </div>
      )}

      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-bold text-gray-900">Edit Offer — {editModal.title}</h3>
              <button onClick={() => setEditModal(null)} className="p-1.5 hover:bg-gray-100 rounded-lg" data-testid="admin-edit-offer-close">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input type="text" value={editModal.title || ''}
                  onChange={e => setEditModal({ ...editModal, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                  data-testid="admin-offer-title-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea rows={3} value={editModal.description || ''}
                  onChange={e => setEditModal({ ...editModal, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                  data-testid="admin-offer-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount %</label>
                  <input type="number" min="0" max="100" step="1"
                    value={editModal.discountPercentage ?? ''}
                    onChange={e => setEditModal({ ...editModal, discountPercentage: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                    data-testid="admin-offer-discount-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid until *</label>
                  <input type="date"
                    value={dateForInput(editModal.validUntil)}
                    onChange={e => setEditModal({ ...editModal, validUntil: e.target.value ? `${e.target.value}T23:59:59` : '' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                    data-testid="admin-offer-valid-until-input" />
                  <p className="text-[10px] text-gray-500 mt-1">Admins can extend dates freely.</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Terms &amp; conditions</label>
                <textarea rows={2} value={editModal.terms || ''}
                  onChange={e => setEditModal({ ...editModal, terms: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-400 outline-none"
                  data-testid="admin-offer-terms-input" />
              </div>
              <label className="flex items-center gap-2 mt-2">
                <input type="checkbox"
                  checked={editModal.isVisible !== false}
                  onChange={e => setEditModal({ ...editModal, isVisible: e.target.checked })}
                  data-testid="admin-offer-visible-checkbox" />
                <span className="text-sm text-gray-700">Publicly visible</span>
              </label>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t bg-gray-50">
              <button onClick={() => setEditModal(null)}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={saveEdit} disabled={!editModal.title || !editModal.validUntil || actionLoading === `edit-${editModal.id}`}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="admin-offer-save-btn">
                {actionLoading === `edit-${editModal.id}` && <RefreshCw className="h-3 w-3 animate-spin" />}
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete offer?</h3>
            <p className="text-sm text-gray-600 mb-1">This will permanently remove the offer:</p>
            <p className="font-semibold text-gray-900 mb-4">"{confirmDelete.title}"</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg">
                Cancel
              </button>
              <button onClick={deleteOffer} disabled={actionLoading === `delete-${confirmDelete.id}`}
                className="flex-1 px-4 py-2 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="admin-offer-confirm-delete-btn">
                {actionLoading === `delete-${confirmDelete.id}` && <RefreshCw className="h-3 w-3 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOffersTab;
